export const CONNECT_CONSENT_PATH = '/connect/consent';
export const CONSENT_INTERACTION_QUERY_KEY = 'interaction';

// oauth-provider's interaction lifetime: a return older than this would only show the expired screen.
export const CONSENT_RETURN_TTL_MS = 10 * 60 * 1000;

const STORAGE_KEY = 'connector-consent-return';

type ConsentReturn = { interaction: string; savedAt: number };

export function isConnectConsentPath(pathname: string): boolean {
  return pathname.replace(/\/+$/, '') === CONNECT_CONSENT_PATH;
}

export function consentInteractionFromLocation(
  pathname: string,
  search: string,
): string | undefined {
  if (!isConnectConsentPath(pathname)) {
    return undefined;
  }
  const interaction = new URLSearchParams(search).get(CONSENT_INTERACTION_QUERY_KEY);
  return interaction || undefined;
}

// Per tab (sessionStorage): the ff-ui-v2 login bounce comes back in the same tab, and another tab
// cannot replay it.
export function saveConsentReturn(interaction: string, now = Date.now()): void {
  const value: ConsentReturn = { interaction, savedAt: now };
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage unavailable (private mode, blocked site data): the plain login bounce still works.
  }
}

// Reads and removes the pending return; a stale one is dropped.
export function takeConsentReturn(now = Date.now()): string | undefined {
  const value = read();
  clearConsentReturn();
  if (!value || now - value.savedAt > CONSENT_RETURN_TTL_MS) {
    return undefined;
  }
  return value.interaction;
}

export function clearConsentReturn(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

function read(): ConsentReturn | undefined {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return undefined;
    }
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as ConsentReturn).interaction === 'string' &&
      typeof (parsed as ConsentReturn).savedAt === 'number'
    ) {
      return parsed as ConsentReturn;
    }
  } catch {
    // Unreadable value: treated as no pending return.
  }
  return undefined;
}

// A top-level navigation, never a fetch: oauth-provider's interaction cookie has to travel with it.
export function leaveForResume(resumeUrl: string): void {
  window.location.assign(resumeUrl);
}
